import { Injectable, signal } from '@angular/core';
import { buildAmendmentWidgetId, parseAmendmentFieldId } from './amendment-field.model';
import { asArray, readKey } from './amendment-value.util';

/**
 * Marker key for a widget that is one whole field (no field key).
 */
const WHOLE_WIDGET = '';

/**
 * Knows which fields of the displayed form carry the amendment indicator.
 *
 * Provided by the form presenter so every widget it renders shares one instance. Markers are
 * keyed by the widget object of the live form data plus a field key. Widgets rebuild their inner
 * row objects freely, but the widget object itself lives as long as the loaded form, so it is
 * the one identity that survives edits.
 */
@Injectable()
export class AmendmentFieldMarkersService {
  private readonly markers = signal<WeakMap<object, ReadonlySet<string>>>(new WeakMap());
  private readonly enabled = signal(false);

  /**
   * Turns the indicators on or off. They are off until the hosting screen opts in.
   */
  public setEnabled(enabled: boolean): void {
    this.enabled.set(enabled);
  }

  /**
   * Marks the fields of `formData` whose ids are in `changedFieldIds`, replacing any previous
   * markers.
   */
  public setMarkers(
    formData: unknown,
    changedFieldIds: readonly string[] | null | undefined,
  ): void {
    const widgetsById = new Map<string, object>();

    asArray(readKey(formData, 'sections')).forEach((section, sectionIndex) => {
      asArray(readKey(section, 'widgets')).forEach((widget, widgetIndex) => {
        if (widget && typeof widget === 'object') {
          widgetsById.set(buildAmendmentWidgetId(sectionIndex, widgetIndex), widget);
        }
      });
    });

    const markers = new WeakMap<object, Set<string>>();

    for (const id of changedFieldIds ?? []) {
      const parsed = parseAmendmentFieldId(id);
      const widget = parsed ? widgetsById.get(parsed.widgetId) : undefined;

      if (!parsed || !widget) {
        continue;
      }

      const keys = markers.get(widget) ?? new Set<string>();
      keys.add(parsed.fieldKey ?? WHOLE_WIDGET);
      markers.set(widget, keys);
    }

    this.markers.set(markers);
  }

  /**
   * Removes every marker.
   */
  public clear(): void {
    this.markers.set(new WeakMap());
  }

  /**
   * Whether any of `fieldKeys` of `widget` was amended. An empty list asks about the widget as
   * a whole, and a widget amended as a whole counts as amended for every one of its keys.
   */
  public isMarked(widget: object | null | undefined, fieldKeys: readonly string[] = []): boolean {
    if (!this.enabled() || !widget) {
      return false;
    }

    const keys = this.markers().get(widget);

    if (!keys) {
      return false;
    }

    if (keys.has(WHOLE_WIDGET)) {
      return true;
    }

    return fieldKeys.some((fieldKey) => keys.has(fieldKey));
  }
}
